/**
 * @lines98/client/screens/gnomes-screen — Плашка «Гномы» (мета-surface): СТОПКА 7
 * горизонтальных карточек-гномов + ресурс-шапка как у «Мастерской».
 *
 * **owner-directive 2026-06-14 «точная копия Мастерской»:** экран рендерит те же горизонтальные
 * плашки-трофеи что и Мастерская, только вместо зверей — спрайты гномов БЕЗ кольца камней.
 * Карточка гнома (`components/gnome-card`) — ветка того же `trophy-card` (ствол), что
 * `jewelry-card`; форма идентична; данные — `GnomeState` (7 цветов).
 *
 * «Тонкая ветка» (CLAUDE.md «ствол → ветки», code-structure §1): экран монтирует ОБЩИЕ
 * модули и задаёт только композицию/внешний вид — РОВНО как `workshop-screen`. Балансы —
 * `lib/currency-hud`; лента камней — `components/gem-pool-hud`; карточка — `components/
 * gnome-card` (горизонтальная плашка = jewelry-card по форме); деталь — `components/
 * gnome-detail` (drill-down как у зверей); описание — `components/info-popover`.
 *
 * **«Мастерская» (legacy 5 зверей) НЕ трогается** — это отдельная поверхность.
 *
 * **UI-FIRST (GN-1/2/6, owner-approved 2026-06-13):** server-блок `gnomes` ещё НЕ
 * реализован (GN-4). 7 карточек рендерятся из ПЛЕЙСХОЛДЕР-модели (`lib/gnome-state`): по
 * умолчанию все 7 на L1; демо-микс (`?state=mix`) для скриншот-baseline.
 * Когда GN-4 даст блок `gnomes`, экран подменит источник без переписывания карточки
 * (паттерн pending → мутация → ресинк как у workshop-screen.handleAction).
 *
 * **Instant-hydration (реюз `lib/meta-snapshot`, как `workshop-screen`):** скелет-awaiting
 * при первом заходе без снимка (CSS `--awaiting` → opacity fade при первом ресинке).
 *
 * **F5-гидрация: НЕ мелькать ложным all-locked (§C тикета fe-playtest-round2, owner-bug
 * 2026-06-22).** Снимок keyed по `playerId` (`getCurrent()?.playerId`). На F5/прямой загрузке
 * `mountGnomesScreen` монтируется СИНХРОННО ДО резолва `bootIdentity()` (IndexedDB async) →
 * `getCurrent()` = null/аноним → ключ снимка не совпал → `loadMetaSnapshot()` = null →
 * placeholder all-locked. Реально-открытые гномы мелькали силуэтами, открываясь лишь при
 * переключении вкладок рейла (поздний reconcile). ФИКС — паттерн Профиля П7 (profile-screen.ts:
 * 890-898): пока identity НЕ зарезолвлена и валидного снимка нет, карточки НЕ вставляются в
 * DOM (нейтральный awaiting-gate, НЕ ложный all-locked); `load()` делает `await bootIdentity()`
 * ПЕРЕД reconcile, ПОСЛЕ резолва ПЕРЕ-читает снимок per-player ключом и вставляет реальное
 * состояние. Настоящий гость (0 открытых) после резолва видит корректный all-locked (AC-C.3).
 * Демо-routes (`?state=`/`?reveal=`) детерминированы → вставляются сразу (AC-C.5).
 *
 * **Гномы без «вырастания» / zero-CLS (feat/fe-hydration п.5 / AC-7/AC-9, owner-bug):**
 * `--gnc-frame-aspect` (единая ширина рамки-героя) ставится на `.gnomes-list` ДО ПЕРВОГО
 * ПЭЙНТА карточек: тёплый путь — синхронно из декода (`peekMaxGnomeFrameAspect`), холодный —
 * await `loadMaxGnomeFrameAspect` ПЕРЕД append. РАНЬШЕ var ставился async по `img.load` ПОСЛЕ
 * append → ширина прыгала с CSS-fallback (1/2) на измеренный max = «вырастание». Аспект
 * остаётся ИЗМЕРЕННЫМ (max natural w/h 7 PNG; R-4), без верхних капов; reduce-motion — без
 * scale (gnome-card.css @media reduce). Единый preload-ствол `lib/asset-preload` (R-3).
 *
 * Layout/visual — styles/gnomes.css (= workshop.css по структуре; layout-plan в шапке).
 *
 * Cross-references:
 *   - lib/currency-hud (HUD валют), components/gem-pool-hud (лента камней),
 *     components/gnome-card (ветка trophy-card) / components/gnome-detail (деталь) / info-popover.
 *   - lib/gnome-state (модель), lib/meta-snapshot + services/meta-api (server-truth балансы/пул).
 *   - screens/workshop-screen.ts (ЭТАЛОН: структура шапки / instant-hydration / drill-down).
 */

import '../styles/gnomes.css';

import type { ColorIndex } from '@lines98/shared/types/game';

import { createGnomeCard, type GnomeCardCost, type GnomeCardHandle } from '../components/gnome-card.js';
import {
  mountGnomeDetail,
  type GnomeDetailHandle,
  type GnomeDetailState,
} from '../components/gnome-detail.js';
import { mountGemPoolHud, type GemPoolHudHandle } from '../components/gem-pool-hud.js';
import { mountInfoPopover, type InfoPopoverHandle } from '../components/info-popover.js';
import { confirmDialog } from '../components/confirm-dialog.js';
import { preloadMetaAssets } from '../lib/asset-preload.js';
import { loadMaxGnomeFrameAspect, peekMaxGnomeFrameAspect } from '../lib/gnome-frame-aspect.js';
import { mountCurrencyHud, type CurrencyHudHandle } from '../lib/currency-hud.js';
import {
  PLACEHOLDER_GNOMES,
  buildGnomes,
  demoGnomes,
  demoGnomesPartial,
  gnomeUpgradeCost,
  isMaxLevel,
  newlyUnlockedColors,
  nextUnlockLevel as normalizeNextUnlock,
  type GnomeState,
} from '../lib/gnome-state.js';
import {
  gnomeCardSingleMode,
  gnomeSingleCellHeightFit,
  SINGLE_CELL_CQW_DIVISOR,
} from '../lib/gnome-card-fit.js';
import { REVEALING_CLASS, revealGnomes, type RevealSlot } from '../lib/gnome-reveal.js';
import { saveLastSeenUnlocks } from '../lib/gnome-unlock-badge.js';
import { onIdentityChanged } from '../lib/identity-events.js';
import { t } from '../lib/i18n/index.js';
import { loadMetaSnapshot, type MetaSnapshot } from '../lib/meta-snapshot.js';
import { emitMetaStateGranted } from '../lib/meta-state-events.js';
import { attachTitleFit } from '../lib/title-fit.js';
import {
  prestigeGnome,
  upgradeGnome,
  type MetaStateSuccess,
} from '../services/meta-api.js';
import { bootIdentity } from '../services/identity.js';
import { getMetaStateShared } from '../services/meta-prefetch.js';
import { useGameStore } from '../store/game-store.js';
import type { ScreenHandle } from './board-screen.js';

/**
 * Сколько проходов «мерь→ужми» делает height-fit крупной (single) карточки на низком портрете.
 * Высота карточки ∝ cell только пока элементы выше CSS-floor'ов; ниже — текст/кнопка/иконки
 * перестают уменьшаться, и один пропорциональный шаг пере-оценивает выигрыш → нужно несколько
 * монотонно-сходящихся проходов. 4 практически достаточно (каждый проход забирает основную часть
 * остаточного overflow); ограничитель страхует от теоретического зацикливания. tunable.
 */
const SINGLE_HEIGHT_FIT_MAX_PASSES = 4;

/**
 * Принудительный синхронный reflow элемента (чтение layout-свойства). Зачем (feat/fe-hydration
 * п.5 / AC-9): после входа в single-режим (`container-type:inline-size` + `--cell-size:25cqw`)
 * браузеру нужен layout-pass, чтобы разрешить cqw-потомков; этот read форсит его В ТОТ ЖЕ кадр →
 * первый ResizeObserver-замер рамки-героя = финальный (нет промежуточного «узкого» кадра). Read-
 * only side-effect-free (вынесено в функцию, чтобы намерение было явным, не «мусорный» доступ).
 */
function forceReflow(el: HTMLElement): void {
  // Присваивание (а не «голое» выражение) делает намеренный layout-read явным: чтение
  // offsetWidth форсит синхронный reflow. void — значение не нужно, только сам side-effect.
  void el.offsetWidth;
}

/** Прочитать query-параметр (defensive — SSR / нет window). */
function queryParam(name: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/** Учитывать prefers-reduced-motion (DI в плеер reveal; reduce → мгновенно, AC-16). */
function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;
  } catch {
    return false;
  }
}

/**
 * Построить 7 слотов гномов из server-truth-блока `gnomes[]` (AC-12: недостающие цвета →
 * locked). Стабильный порядок 1..7 (раскладка не «прыгает»). Server-truth (I-Gn4).
 */
function gnomesFromServer(gnomes: MetaStateSuccess['gnomes']): GnomeState[] {
  const byColor = new Map<number, MetaStateSuccess['gnomes'][number]>();
  for (const g of gnomes) byColor.set(g.color, g);
  return buildGnomes((color) => {
    const g = byColor.get(color);
    return g && g.unlocked
      ? { level: g.level, prestigeCount: g.prestigeCount, unlocked: true }
      : { level: 1, prestigeCount: 0, unlocked: false };
  });
}

/**
 * Построить 7 слотов из мгновенного снимка (instant-hydration, AC-9). Снимок хранит
 * открытые слоты; остальные достраиваются как locked (AC-12). null-снимок → все locked.
 */
function gnomesFromSnapshot(snapshot: MetaSnapshot | null): GnomeState[] {
  if (!snapshot) return [...PLACEHOLDER_GNOMES];
  const byColor = new Map<number, MetaSnapshot['gnomes'][number]>();
  for (const g of snapshot.gnomes) byColor.set(g.color, g);
  return buildGnomes((color) => {
    const g = byColor.get(color);
    return g && g.unlocked
      ? { level: g.level, prestigeCount: g.prestigeCount, unlocked: true }
      : { level: 1, prestigeCount: 0, unlocked: false };
  });
}

/**
 * Стартовая модель ДО ресолва сети. Демо-route (screenshot-baseline §0.D):
 *   `?demo=gnomes&state=locked`   — все 7 locked (новый игрок).
 *   `?demo=gnomes&state=partial`  — демо-микс (открытые + locked).
 *   `?demo=gnomes&state=allopen`  — все 7 открыты.
 *   `?demo=gnomes&reveal=<colors>`— стартуем locked для тех цветов (reveal проиграет их).
 *   `?state=mix` (legacy)         — демо-микс.
 * Иначе — мгновенный снимок (или placeholder all-locked). Server-truth подменяет при reconcile.
 */
function initialGnomes(snapshot: MetaSnapshot | null): GnomeState[] {
  const state = queryParam('state');
  if (state === 'allopen') {
    return buildGnomes(() => ({ level: 6, prestigeCount: 0, unlocked: true }));
  }
  if (state === 'partial') return demoGnomesPartial();
  if (state === 'mix') return demoGnomes();
  if (state === 'locked') return [...PLACEHOLDER_GNOMES];
  // reveal=<colors> без state → стартуем all-locked, чтобы вспышка была видна.
  if (queryParam('reveal')) return [...PLACEHOLDER_GNOMES];
  return gnomesFromSnapshot(snapshot);
}

/** Распарсить `reveal=2,5,7` → массив цветов 1..7 (демо-хук reveal, §0.D). */
function parseRevealColors(): number[] {
  const raw = queryParam('reveal');
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((c) => Number.isInteger(c) && c >= 1 && c <= 7);
}

/** Опции монтажа плашки «Помощники». */
export interface GnomesScreenOptions {
  /**
   * companion-focus (тикет 2026-06-28): deep-link фокус на конкретном помощнике — экран
   * доскроллит его карточку в видимую полосу `.gnomes-scroll` + транзиентно подсветит.
   * Приходит store-payload'ом `gnomesFocusColor` (mirror leaderboardAllTimeMode), читается
   * app.ts mount-веткой. Потребляется ОДИН раз (экран сразу очищает store-поле → re-mount
   * на смене языка фокус НЕ повторяет, AC-3). absent → обычный заход (верх списка).
   */
  readonly focusColor?: ColorIndex;
}

/** Смонтировать плашку «Гномы» (= раскладка «Мастерской», 7 гномов, drill-down). */
export function mountGnomesScreen(host: HTMLElement, opts: GnomesScreenOptions = {}): ScreenHandle {
  host.innerHTML = '';
  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  root.className = 'gnomes-screen screen-root';
  // Структура = workshop-screen один-в-один: header (title+«?»+HUD+камни) / scroll(list) / state.
  root.innerHTML = `
    <header class="gnomes-header">
      <div class="gnomes-title-row">
        <h2 class="gnomes-title screen-title">${t('game.gnomes.title')}</h2>
        <span class="gnomes-info-host"></span>
      </div>
      <div class="gnomes-hud-host"></div>
      <div class="gnomes-gems-host"></div>
    </header>
    <div class="gnomes-scroll">
      <p class="gnomes-next-unlock" role="status" hidden></p>
      <div class="gnomes-list" role="list" aria-label="${t('game.gnomes.listAria')}"></div>
    </div>
    <div class="gnomes-state" role="status" aria-live="polite" hidden></div>
  `;
  host.appendChild(root);

  const infoHost = root.querySelector<HTMLElement>('.gnomes-info-host')!;
  const hudHost = root.querySelector<HTMLElement>('.gnomes-hud-host')!;
  const gemsHost = root.querySelector<HTMLElement>('.gnomes-gems-host')!;
  const listEl = root.querySelector<HTMLElement>('.gnomes-list')!;
  const scrollEl = root.querySelector<HTMLElement>('.gnomes-scroll');
  const stateEl = root.querySelector<HTMLElement>('.gnomes-state')!;
  const nextUnlockEl = root.querySelector<HTMLElement>('.gnomes-next-unlock')!;

  // companion-focus (AC-1/AC-3): deep-link фокус-цель приходит store-payload'ом
  // (mirror leaderboardAllTimeMode); app.ts читает его и передаёт сюда. Потребление ДЕТЕРМИНИРОВАНО
  // (не гонка): store-поле очищается НЕ при mount и НЕ при показе, а при НАСТОЯЩЕМ снятии подсветки
  // (первое взаимодействие / авто-таймаут — consumeFocus). Почему: boot c `?lang=` форсит re-mount
  // фазы (main.ts bootInitialLocale→rerenderActivePhaseForLocale; app.ts LOCALE_CATALOG_READY) —
  // пока цель не «прожита», каждый re-mount ПЕРЕ-вычитывает её из store (app.ts) и пере-целится →
  // фокус не теряется на boot, без «кто успел применить». После dismiss store=null → последующая
  // смена языка (AC-3) читает null и фокус НЕ повторяет.
  const focusColor: ColorIndex | null = opts.focusColor ?? null;

  // Прогреть мета-спрайты (гномы card + full-арт детали / камни / валюты) — карточки/деталь
  // рисуются мгновенно без повторного декода при пересборке DOM (fix «вщёлкивания»). Идемпотентно.
  void preloadMetaAssets();

  // Синхронный снимок мета-динамики (балансы + пул) — мгновенный показ ДО ресолва сети
  // (тот же кэш, что Мастерская; server-truth — следующий getMetaState, reconcile).
  const snapshot: MetaSnapshot | null = loadMetaSnapshot();

  // helper-unlock A2 сброс (AC-10): заход на экран «Помощники» = игрок «заглянул» → пишем
  // last-seen := текущий granted и эмитим → рейл-бейдж гаснет МГНОВЕННО при mount (не ждём
  // reconcile). Источник granted при mount — снимок (server-truth, instant-hydration); после
  // reconcile (load) last-seen переподтверждается свежим server-truth granted. last-seen — per-
  // player через снимок-ствол (НЕ ad-hoc localStorage-ключ, AC-14).
  const resetBadge = (granted: number): void => {
    saveLastSeenUnlocks(granted);
    emitMetaStateGranted(granted); // рейл сравнит granted с обновлённым last-seen → dot снят
  };
  if (snapshot) resetBadge(snapshot.gnomeUnlocksGranted);

  // Скелет первого захода (= workshop-screen): если снимка НЕТ → помечаем `--awaiting`
  // → CSS приглушает HUD/пул/карточки (opacity), значения плавно появляются при ресинке.
  if (!snapshot) root.classList.add('gnomes-screen--awaiting');

  // HUD валют (переиспользован — single source). КАРАТ УБРАН (Q6 / AC-Gn12): showKarat:false.
  const hud: CurrencyHudHandle = mountCurrencyHud(hudHost, {
    showKarat: false,
    ...(snapshot ? { initial: { gold: snapshot.gold, shards: snapshot.shards } } : {}),
  });

  // Лента запаса камней (7 счётчиков) — тот же модуль, что в Мастерской/детали зверя.
  const gemHud: GemPoolHudHandle = mountGemPoolHud(gemsHost, {
    ...(snapshot ? { pool: snapshot.gemPool } : {}),
  });

  // «?»-popover описания (шапка-витрина без inline-копии — эталон).
  const infoPopover: InfoPopoverHandle = mountInfoPopover({
    title: t('game.gnomes.title'),
    bodyText: t('game.gnomes.desc'),
    triggerHost: infoHost,
    variant: '?',
  });

  // Демо-route (§0.D screenshots-DoD): `?state=`/`?reveal=` детерминируют вид → его можно
  // показать СРАЗУ (не зависит от identity, AC-C.5: демо-routes не сломаны). В проде параметров
  // нет → флаг false.
  const hasDemoRoute = queryParam('state') !== null || queryParam('reveal') !== null;

  // F5-гидрационный гейт (§C, owner-bug 2026-06-22). Карточки можно показать БЕЗ риска ложного
  // all-locked ТОЛЬКО когда их состояние ДОСТОВЕРНО, а не «по умолчанию locked». Достоверно оно:
  //   (a) есть мгновенный снимок (per-player ключ совпал → реальное состояние сразу), ИЛИ
  //   (b) демо-route (`?state=`/`?reveal=` — вид детерминирован для baseline), ИЛИ
  //   (c) пришёл ПЕРВЫЙ server-truth reconcile (`GET /meta/state` ответил: открытые гномы у
  //       авторизованного / 401 → настоящий гость → корректный all-locked, AC-C.3).
  // На холодном F5 авторизованного БЕЗ снимка (или со снимком, который не прочитался) показывать
  // placeholder all-locked ДО ответа сервера = мелькнуть ложным all-locked у реально-открытого
  // игрока (owner-bug). Поэтому до достоверности — нейтральный awaiting-gate (карточки НЕ в DOM),
  // карточки появляются ОДНИМ кадром уже в реальном состоянии (паттерн Профиля П7: не рисуем
  // ложное состояние до резолва). `viewReconciled` взводит reconcile (server / guest-подтверждение).
  // СТАРТ true (owner-bug 2026-06-22): карточки видны сразу (снимок/placeholder), reconcile
  // дотягивает server-truth. Прежний `false` (C-гейт) оставлял экран пустым.
  let viewReconciled = true;
  let cardsShown = false;
  // `destroyed` объявлен ЗДЕСЬ, до синхронного вызова `tryRevealCards()` на warm-aspect-пути
  // (он читает `destroyed`). Прежде объявлялся ниже (≈560) → на ТЁПЛОМ заходе (PNG уже
  // декодированы → warmAspect≠null → tryRevealCards зовётся синхронно) падало TDZ
  // «Cannot access 'destroyed' before initialization» → mount гномов кидал, экран пустой,
  // URL не менялся (owner-bug 2026-06-22). На холодном заходе ошибки не было (async-путь).
  let destroyed = false;

  // 7 карточек гномов (placeholder/demo — server gnomes block GN-4). Тап по герою → деталь
  // (drill-down, как у зверей). Карточка — ветка trophy-card (= карточка зверя, без камней).
  // СОЗДАЁМ карточки, но НЕ вставляем сразу: вставка — единым шагом `revealCards` ниже
  // (после того как известны и max-аспект рамки, и single-mode) → первый пэйнт карточек уже
  // в ФИНАЛЬНОМ размере (feat/fe-hydration п.5 / AC-9 zero-CLS).
  // Баланс игрока (золото + осколки) — для цены/affordability ПРЯМО НА КАРТОЧКЕ (owner-directive
  // 2026-06-22: «прокачка на ОСНОВНОЙ карточке, цена ВМЕСТО слова, неактивна при нехватке» — НЕ в
  // проваливании-детали). Мгновенно из снимка (instant-hydration), затем server-truth из reconcile.
  let currentBalance: { gold: number; shards: number } = snapshot
    ? { gold: snapshot.gold, shards: snapshot.shards }
    : { gold: 0, shards: 0 };

  /**
   * Опции цены/affordability карточки из ТЕКУЩЕГО баланса (owner-directive 2026-06-22): карточка
   * показывает цену `gnomeUpgradeCost(level)` ВМЕСТО слова «Улучшить» + `▲`, и становится
   * НЕАКТИВНОЙ когда баланса не хватает. На L20 цена не нужна (карточка сама рисует «Максимум») →
   * пустые опции. Числа — из config (`gnomeUpgradeCost`, no-hardcode); баланс — server-truth.
   */
  const cardOpts = (g: GnomeState): { cost?: GnomeCardCost; affordable?: boolean } => {
    if (isMaxLevel(g.level)) return {};
    const cost = gnomeUpgradeCost(g.level);
    const affordable =
      currentBalance.gold >= cost.gold && currentBalance.shards >= cost.shards;
    return { cost, affordable };
  };

  const cards = new Map<ColorIndex, GnomeCardHandle>();
  const gnomeStates = new Map<ColorIndex, GnomeState>();
  const orderedCards: HTMLElement[] = [];
  for (const g of initialGnomes(snapshot)) {
    gnomeStates.set(g.color, g);
    const card = createGnomeCard({
      state: g,
      ...cardOpts(g),
      onAction: (color) => handleAction(color),
      onOpenDetail: (color) => openGnomeDetail(color),
    });
    cards.set(g.color, card);
    orderedCards.push(card.element);
  }

  /**
   * Можно ли сейчас показать карточки. `viewReconciled` стартует TRUE (owner-bug 2026-06-22:
   * прежний C-гейт оставлял экран ПУСТЫМ — карточки не вставлялись, «стало хуже») → карточки
   * показываем сразу, как готов аспект рамки: состояние = снимок (instant, верное) ЛИБО
   * placeholder → reconcile (load) дотягивает server-truth. Пустой экран хуже кратковременного
   * placeholder. Анти-флэш (open-гном не мелькает locked на F5) переделаем мягко — рендером в
   * нейтральном состоянии, НЕ скрытием карточек.
   */
  const readyToShowCards = (): boolean => snapshot !== null || hasDemoRoute || viewReconciled;

  /**
   * Пере-применить состояние всех 7 карточек из СВЕЖЕГО снимка (§C). Зовётся при смене
   * идентичности (per-player снимок другого аккаунта). Обновляет модель + карточку без
   * пересборки (attach-не-перерисовка, §3). Карточки ещё не вставлены → обновляется только их
   * (detached) DOM-состояние; revealCards вставит уже с верным видом. null-снимок → all-locked
   * (модель), но карточки покажутся лишь когда вид станет достоверным (reconcile).
   */
  const rehydrateFromSnapshot = (): void => {
    const fresh = gnomesFromSnapshot(loadMetaSnapshot());
    for (const g of fresh) {
      gnomeStates.set(g.color, g);
      cards.get(g.color)?.update(g, cardOpts(g));
    }
  };

  // Строка-индикатор «следующий гном на уровне N» (AC-3) — server-truth число вехи
  // (NO-HARDCODE, AC-16). Из снимка мгновенно (instant-hydration), затем reconcile.
  // Скрыта, если все 7 открыты (next = null, AC-19). Текст fluid (CSS §8.10).
  let currentNextUnlock: number | null = snapshot
    ? normalizeNextUnlock(snapshot.nextUnlockLevel)
    : null;
  const nextUnlockAnchor = nextUnlockEl.nextSibling; // .gnomes-list — точка возврата в DOM
  function setNextUnlock(next: number | null): void {
    currentNextUnlock = normalizeNextUnlock(next);
    if (currentNextUnlock === null) {
      // Все 7 открыты (AC-19): строка УДАЛЯЕТСЯ из DOM (не просто hidden) — тест ждёт
      // count 0 (скрытый [hidden]-элемент всё равно считался бы 1).
      if (nextUnlockEl.parentNode) nextUnlockEl.parentNode.removeChild(nextUnlockEl);
    } else {
      nextUnlockEl.hidden = false;
      nextUnlockEl.textContent = t('game.gnomes.nextUnlock', { n: currentNextUnlock });
      if (!nextUnlockEl.parentNode) {
        scrollEl?.insertBefore(nextUnlockEl, nextUnlockAnchor);
      }
    }
  }
  setNextUnlock(currentNextUnlock);

  // ОДНА В РЯД → растёт (owner-directive 2026-06-15): когда по ширине влезает только ОДНА
  // карточка, переключаем список в режим --single (CSS растит карточку до ширины доски). Если
  // влезает ≥2 — ничего не меняем. Считаем по натуральной ширине карточки (--gn-card-w) и зазору
  // (--m), читая их из computed-стилей (формула, не magic-number). ResizeObserver — на ресайз/рейл.
  const px = (v: string): number => parseFloat(v) || 0;
  const updateSingleMode = (): void => {
    // СОХРАНИТЬ scroll-позицию через весь measure-цикл (owner-bug 2026-06-24 «гном уезжает в
    // списке при апгрейде; страница как будто обновляется»). Ниже мы СНИМАЕМ `--single` + пин
    // и ПЕРЕ-меряем → все карточки на миг схлопываются в обычный (мелкий) размер → scrollHeight
    // резко падает → браузер КЛАМПИТ scrollTop к новому (меньшему) максимуму. Когда `--single`
    // возвращается и высота растёт обратно, scrollTop НЕ восстанавливается сам — остаётся
    // склампленным ≈ к верху. На телефоне (single-режим, высокие карточки) это «увозит»
    // прокрученного-к гнома вверх после КАЖДОГО апгрейда (relayout → updateSingleMode зовётся из
    // applyGnomeState при ресинке) → игрок теряет место и ищет гнома заново. Чиним: снять снимок
    // scrollTop ДО цикла и вернуть его в finally на ВСЕХ путях выхода (включая early-return).
    // Восстановление синхронно в том же тике (до пэйнта) → визуального прыжка нет.
    const restoreScrollTop = scrollEl ? scrollEl.scrollTop : null;
    try {
      // Замеряем ФАКТИЧЕСКУЮ натуральную ширину карточки (снимаем --single на момент замера —
      // формула недооценивает на мелком размере из-за floor'ов иконок/шрифтов). Один reflow на
      // ресайз, без мигания (paint один раз в конце кадра). RO слушает listEl, его ширина не
      // меняется при toggle → петли нет.
      listEl.classList.remove('gnomes-list--single');
      listEl.style.removeProperty('--gnc-single-cell'); // снять пин на момент замера (см. ниже)
      const card = listEl.querySelector<HTMLElement>('.gnome-card');
      if (!card) return;
      const cardW = card.getBoundingClientRect().width;
      const gap = px(getComputedStyle(root).getPropertyValue('--m'));
      const avail = listEl.clientWidth;
      // Решение «<2 комфортных карточек → крупный режим» — общий модуль (тот же расчёт у оверлея
      // открытия помощника, чтобы карточка-награда совпадала с сеткой на всех вьюпортах).
      const single = gnomeCardSingleMode(avail, cardW, gap);
      listEl.classList.toggle('gnomes-list--single', single);
      if (!single) return;
      // ПИН single-cell ради zero-CLS (feat/fe-hydration п.5 / AC-9). single-режим задаёт
    // `--cell-size: round(down, 100cqw/4, 1px)` (container-query): cqw разрешается браузером за
    // ОТДЕЛЬНЫЙ layout-pass ПОСЛЕ `container-type` → первый ResizeObserver-замер рамки-героя ловит
    // кадр с НЕразрешённым cqw → «прыжок ширины». Лечение: ПРЕ-резолвим формулу сами и пиним
    // КОНКРЕТНЫЙ px на список как `--gnc-single-cell` (CSS делает его override приоритетным) → cell
    // детерминирован с первого пэйнта. `1cqw` = 1% ширины контейнера `.gnome-card` (он несёт
    // container-type:inline-size в single) → widthCell = floor(cardWidth / SINGLE_CELL_CQW_DIVISOR)
    // (делитель = CSS-формула, единый трассируемый источник в lib/gnome-card-fit). РАНЬШЕ пин брал
    // `getComputedStyle(card)['--cell-size']`, но для нерегистрированной custom-property он
    // возвращает СТРОКУ-ВЫРАЖЕНИЕ `round(down, 25cqw, 1px)`, не px → height-fit не мог считать
    // (NaN). Теперь пин — настоящий px.
    forceReflow(listEl);
    const widthCell = Math.floor(card.clientWidth / SINGLE_CELL_CQW_DIVISOR);
    if (widthCell <= 0) return;
    listEl.style.setProperty('--gnc-single-cell', `${widthCell}px`);

    // ВЫСОТО-FIT (owner visual-прогон 2026-06-21 «карточка гнома не влезает на 320×480 / 375×667»):
    // ширино-производный cell (cqw, owner-directive 2026-06-15 «одна в ряд → растёт») на НИЗКОМ
    // портрете делает карточку выше viewport → bonus-pill + низ карточки (.gnome-card-action)
    // уезжают за кадр (P1-нарушение §0.A). Лечим как ДОСКУ: `cell = min(width-bound, height-bound)`
    // (responsive-ui §3 / viewport-sizer). Доступная под карточку высота = от ВЕРХА карточки до НИЗА
    // bounded scroll-региона `.gnomes-scroll` (§1.2): (scrollTop + scrollClientH) − cardTop. Так
    // учитываем и контент над карточкой (HUD/лента/строка «следующий помощник») в её потоке, и
    // реальную нижнюю кромку области.
    //
    // ИТЕРАТИВНО (а не одним шагом): высота карточки ∝ cell ТОЛЬКО пока элементы выше своих
    // CSS-floor'ов; ниже floor'а текст(13px)/кнопка(--btn-h 44px)/иконки(20px) перестают
    // уменьшаться → один пропорциональный шаг ПЕРЕ-оценивает выигрыш. Поэтому мерим→ужимаем
    // несколько раз (монотонно сходится): каждый проход gnomeSingleCellHeightFit считает свежий
    // ratio от уже-применённого cell. Стоп — когда влезло, либо cell уперся в floor читаемости
    // (GNOME_SINGLE_CELL_FLOOR), либо итерации исчерпаны. Floor читаемости держит кнопку тактильной
    // (её собственный --btn-h floor); P1 visibility > P3 no-scroll: остаток (если экран совсем мал)
    // добирает bounded scroll (§1.2), НЕ молчаливая обрезка.
      if (!scrollEl) return;
      let pinnedCell = widthCell;
      for (let pass = 0; pass < SINGLE_HEIGHT_FIT_MAX_PASSES; pass++) {
        forceReflow(listEl);
        const cardRect = card.getBoundingClientRect();
        const scrollRect = scrollEl.getBoundingClientRect();
        const availHeight = scrollRect.top + scrollEl.clientHeight - cardRect.top;
        const fitCell = gnomeSingleCellHeightFit(pinnedCell, cardRect.height, availHeight);
        const next = Math.floor(fitCell);
        // Сошлось (влезло) или дальше не ужать (floor / без изменений) — выходим.
        if (next >= pinnedCell) break;
        pinnedCell = next;
        // Округляем вниз до целого px (паритет с round(down, …, 1px) base-token, §4.2 sub-pixel).
        listEl.style.setProperty('--gnc-single-cell', `${pinnedCell}px`);
      }
    } finally {
      // ВОССТАНОВИТЬ scroll-позицию (см. снимок выше). На всех путях выхода — including early-
      // return после схлопывания списка. Ставим только если реально сместилось (idempotent;
      // не дёргаем scrollTop без нужды). Браузер сам клампит к допустимому максимуму, если
      // высота стала меньше (контент уменьшился) — но к этому моменту --single уже вернулся,
      // высота восстановлена, значение валидно.
      if (scrollEl && restoreScrollTop !== null && scrollEl.scrollTop !== restoreScrollTop) {
        scrollEl.scrollTop = restoreScrollTop;
      }
    }
  };
  // Relayout = single-mode toggle (owner-directive 2026-06-15). 7 слотов гномов (открытые +
  // locked) при нехватке высоты доступны bounded inner-scroll региона `.gnomes-scroll`
  // (§1.2 / тикет AC-2 — НЕ молчаливая обрезка). RO — на ресайз/рейл.
  const relayout = (): void => {
    updateSingleMode();
  };

  // ===========================================================================
  // companion-focus — deep-link фокус на конкретном помощнике (тикет 2026-06-28).
  // Скролл целевой карточки в видимую полосу bounded-региона `.gnomes-scroll` (НЕ page-scroll,
  // AC-6) + транзиентная подсветка `.gnome-card--focused` (AC-9/10/11). Применяется ОДИН раз
  // после settle гидрации/reconcile (scrollHeight финальный, не конфликтует с restoreScrollTop
  // в updateSingleMode, AC-8). reduce-motion → мгновенно (AC-12). Локальная логика (одно место-
  // потребитель — тикет §«Модульность» п.6: вынос `lib/scroll-focus` при 2-м потребителе).
  // ===========================================================================
  let focusApplied = false; // one-shot: применяем скролл+подсветку строго один раз
  let serverReconciled = false; // server-truth получен → состояние карточек достоверно (фокус ждёт его)
  let focusDismissTimer: ReturnType<typeof setTimeout> | null = null;
  let detachFocusInteractions: (() => void) | null = null;

  /** Прочитать мс-длительность из CSS-токена (единый источник, не magic-ms). fallback — floor. */
  const focusMsToken = (name: string, fallback: number): number => {
    const n = parseFloat(getComputedStyle(root).getPropertyValue(name));
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  /**
   * Снять ВИЗУАЛ подсветки + слушатели/таймер (БЕЗ потребления store). Зовётся при dismiss И на
   * unmount: на unmount store НЕ трогаем намеренно — пока цель не «прожита» (показана и снята),
   * она остаётся в store, чтобы выживший re-mount (boot-перемонтаж по готовности lazy-каталога)
   * пере-нацелился. Потребление store — отдельно (consumeFocus), только при настоящем dismiss.
   */
  const clearFocusHighlight = (): void => {
    if (focusDismissTimer !== null) {
      clearTimeout(focusDismissTimer);
      focusDismissTimer = null;
    }
    if (detachFocusInteractions) {
      detachFocusInteractions();
      detachFocusInteractions = null;
    }
    if (focusColor !== null) cards.get(focusColor)?.element.classList.remove('gnome-card--focused');
  };

  /**
   * Потребить фокус-цель (AC-3 one-shot): чистим store-поле. Зовётся ТОЛЬКО при НАСТОЯЩЕМ снятии
   * подсветки — первое взаимодействие игрока (скролл/тап) ИЛИ авто-таймаут. ИМЕННО ТАК: пока цель
   * не снята, store держит её → каждый boot re-mount (готовность каталога / смена языка В ПРОЦЕССЕ
   * прихода) ПЕРЕ-вычитывает 7 и пере-целится (detерминизм, без гонки «кто успел применить»);
   * после dismiss store = null → последующая смена языка фокус НЕ повторяет (AC-3).
   */
  const consumeFocus = (): void => {
    useGameStore.setState({ gnomesFocusColor: null });
  };

  /** Полный dismiss: снять визуал + потребить цель (первое взаимодействие / авто-таймаут, AC-9). */
  const dismissFocus = (): void => {
    clearFocusHighlight();
    consumeFocus();
  };

  /**
   * Навесить подсветку целевой карточки + завести авто-снятие/«первое взаимодействие» (AC-9).
   * Слушатели взаимодействия ВООРУЖАЕМ лишь после короткого окна --gn-focus-arm-ms: наш
   * СОБСТВЕННЫЙ программный settle-скролл (хвост scrollIntoView / restore scrollTop в
   * updateSingleMode/relayout) тоже эмитит 'scroll' — без arm-окна он снял бы подсветку в тот же
   * кадр (и потребил бы цель). Окно < hold; реальное взаимодействие игрока приходит позже.
   */
  const highlightFocus = (el: HTMLElement): void => {
    if (destroyed) return;
    el.classList.add('gnome-card--focused');
    focusDismissTimer = setTimeout(dismissFocus, focusMsToken('--gn-focus-hold-ms', 3000));
    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, focusMsToken('--gn-focus-arm-ms', 400));
    const onInteract = (): void => {
      if (armed) dismissFocus();
    };
    scrollEl?.addEventListener('scroll', onInteract, { passive: true });
    listEl.addEventListener('pointerdown', onInteract);
    detachFocusInteractions = (): void => {
      clearTimeout(armTimer);
      scrollEl?.removeEventListener('scroll', onInteract);
      listEl.removeEventListener('pointerdown', onInteract);
    };
  };

  /**
   * Применить deep-link фокус ОДИН раз. Гейт: цель задана, server-truth достоверен, карточка в DOM.
   * scrollIntoView внутри bounded-предка `.gnomes-scroll` (screen-root overflow:hidden → НЕ
   * page-scroll, AC-6); block:'nearest' — не дёргаем экран, если карточка уже целиком видна
   * (AC-5); scroll-padding-top контейнера + scroll-margin-top карточки уводят её из-под
   * collapsed-rail ☰ (AC-7). Фокус ≠ открытие detail (AC-4) — только скролл+подсветка карточки
   * в списке.
   *
   * Скролл МГНОВЕННЫЙ (behavior:'auto') НАМЕРЕННО: целевая карточка может быть далеко (нижний
   * гном на коротком вьюпорте) — затяжной smooth-скролл (а) перекрывал бы arm-окно, и его хвост
   * scroll-событий снимал бы подсветку, (б) делал бы band-позицию недетерминированной к моменту
   * проверки. Deep-link «приземление сразу на карточке» — корректный UX; «плавность» несёт fade-in
   * самой подсветки (transition outline/box-shadow). reduce-motion и так был мгновенный (AC-12).
   * Позиция выставляется синхронно → подсветку вешаем сразу на финальной позиции (band детерминирован).
   */
  const applyFocus = (): void => {
    if (focusApplied || destroyed || focusColor === null || !serverReconciled) return;
    const el = cards.get(focusColor)?.element;
    if (!el || !el.parentNode) return; // карточки ещё не в DOM — повторим на следующем settle
    focusApplied = true;
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    highlightFocus(el);
  };

  /**
   * Вставить карточки в список ОДНИМ синхронным шагом, КОГДА уже известны и max-аспект рамки,
   * и single-mode → первый пэйнт карточек сразу в ФИНАЛЬНОМ размере (feat/fe-hydration п.5 /
   * AC-9 zero-CLS). Порядок внутри одного тика (без промежуточного пэйнта): (1) ставим
   * `--gnc-frame-aspect` на список (ширина рамки-героя финальна); (2) вставляем карточки;
   * (3) `updateSingleMode` (карточка «одна в ряд → растёт», owner 2026-06-15) — тоже ДО пэйнта,
   * иначе на узком телефоне карточка скакнула бы с обычной ширины на board-ширину. Идемпотентно.
   *
   * @param aspect — измеренный max-аспект (NOT хардкод, AC-10) для `--gnc-frame-aspect`.
   */
  const revealCards = (aspect: number): void => {
    cardsShown = true;
    // MOUNT-FRAME guard (owner-bug 2026-06-20 «гномы/камни через призму»): класс `--mounting`
    // выключает переходы спрайта/силуэта (gnome-card.css) НА ВРЕМЯ первого пэйнта. Без него
    // синхронный пин `--cell-size` ниже (updateSingleMode) меняет высоту героя/locked-filter
    // ПОСЛЕ вставки → `transition: height/filter` анимируют крупный full-PNG покадрово →
    // GPU ловит частично загруженную текстуру = «битые цветные клетки». Ставим ДО append,
    // снимаем двойным rAF после первого пэйнта (см. ниже). Идемпотентно (toggle на классе).
    listEl.classList.add('gnomes-list--mounting');
    listEl.style.setProperty('--gnc-frame-aspect', String(aspect));
    for (const el of orderedCards) {
      if (!el.parentNode) listEl.appendChild(el);
    }
    // single-mode + пин single-cell ДО первого пэйнта карточек (нет скачка ширины). Внутри —
    // forceReflow + считывание разрешённого cqw-cell и его пин (см. updateSingleMode): первый
    // ResizeObserver-замер рамки-героя уже финальный (feat/fe-hydration п.5 / AC-9 zero-CLS).
    updateSingleMode();
    // Снять mount-guard ПОСЛЕ первого пэйнта: 1-й rAF — перед пэйнтом этого кадра, 2-й — после
    // его коммита → карточки УЖЕ нарисованы в финальном состоянии, переходы снова активны для
    // hover/reveal. destroyed-guard: экран мог уйти. SSR/без rAF — переходы просто не подавлены
    // (на сервере нет GPU-рендера, безвредно).
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!destroyed) listEl.classList.remove('gnomes-list--mounting');
        });
      });
    } else {
      listEl.classList.remove('gnomes-list--mounting');
    }
    // companion-focus: карточки теперь в DOM. Если server-truth уже достоверен (cold-path:
    // load() реконсилнул состояние ДО резолва аспекта), применяем фокус сейчас. Иначе applyFocus
    // — no-op (ждёт serverReconciled), его повторит mount-`.then` после load(). one-shot.
    applyFocus();
  };

  // Аспект рамки готов ДО первого пэйнта карточек (AC-7/AC-9). Тёплый путь (boot уже прогрел
  // `preloadMetaAssets`) — синхронно из декода (`peekMaxGnomeFrameAspect`, R-3); холодный —
  // await кеш-промиса декода 7 PNG (`loadMaxGnomeFrameAspect`, R-4) ПЕРЕД вставкой карточек.
  // Каркас шапки/HUD уже отрисован синхронно (P1 видимость не страдает). Источник natural-
  // размеров — те же спрайты, что рисует карточка (gnomeFullGlyph; измерено, не хардкод, AC-10).
  let resolvedAspect: number | null = null;

  /**
   * Вставить карточки, КОГДА готовы ОБА условия (§C + zero-CLS): (1) известен max-аспект рамки
   * (`resolvedAspect`, иначе ширина-героя прыгнула бы); (2) вид валиден без риска ложного
   * all-locked (`readyToShowCards()` — снимок/демо/identity-резолв). Идемпотентно (`cardsShown`):
   * зовётся из аспект-резолва И из `load()` после `await bootIdentity` — кто будет вторым,
   * тот и вставит, первый — no-op. На F5-холоде без снимка карточки появляются ОДНИМ кадром в
   * реальном состоянии после резолва identity, а не мелькают placeholder all-locked.
   */
  const tryRevealCards = (): void => {
    if (cardsShown || destroyed) return;
    if (resolvedAspect === null) return;
    if (!readyToShowCards()) return;
    revealCards(resolvedAspect);
  };

  const warmAspect = peekMaxGnomeFrameAspect();
  if (warmAspect !== null) {
    resolvedAspect = warmAspect;
    tryRevealCards();
  } else {
    void loadMaxGnomeFrameAspect().then((max) => {
      if (destroyed) return;
      resolvedAspect = max;
      tryRevealCards();
    });
  }

  // RO — single-mode toggle на ресайз/рейл (после первичной вставки карточек). На пустом
  // списке (карточки ещё не вставлены в cold-path) `updateSingleMode` сам no-op (нет .gnome-card).
  const ro = new ResizeObserver(() => relayout());
  ro.observe(listEl);

  // Активная карточка-деталь (overlay) — null когда закрыта.
  let detail: GnomeDetailHandle | null = null;
  let openDetailColor: ColorIndex | null = null; // цвет открытой детали (для refresh после reconcile)
  // (`destroyed` объявлен выше — до синхронного tryRevealCards на warm-пути, anti-TDZ.)

  // (currentBalance + cardOpts объявлены выше — до создания карточек, чтобы цена/affordability
  // шли в карточку с первого кадра, owner-directive 2026-06-22.)

  // Заголовок autofit (единый размер, как другие экраны).
  const titleFit = attachTitleFit(root);

  /** Показать/спрятать экранный статус (загрузка / гость / ошибка сети). */
  function setScreenState(message: string | null): void {
    if (!message) {
      stateEl.hidden = true;
      stateEl.textContent = '';
      return;
    }
    stateEl.hidden = false;
    stateEl.textContent = message;
  }

  // In-flight guard per-color (AC-8): пока мутация цвета X в полёте, повторный тап (на карточке
   // ИЛИ детали того же гнома) НЕ инициирует второй сетевой запрос. Один источник истины о полёте
   // для ОБЕИХ поверхностей (карточка + деталь) — Set цветов «в полёте».
  const inFlight = new Set<ColorIndex>();

  /**
   * Поставить/снять pending для гнома `color` НА ОБЕИХ поверхностях консистентно (AC-8):
   * карточка (`gnome-card.setPending`) + открытая деталь того же цвета (`detail.setPending`).
   * Один источник истины о полёте → нет рассинхрона pending между карточкой и деталью.
   */
  function setGnomePending(color: ColorIndex, pending: boolean): void {
    cards.get(color)?.setPending(pending);
    if (detail && openDetailColor === color) detail.setPending(pending);
  }

  /**
   * Оптимистичный показ апгрейда (instant-hydration §3, AC-5): ДО ответа сервера карточка/деталь
   * показывают `level+1`, цена пересчитывается под новый уровень, баланс HUD уменьшается на
   * ОТОБРАЖЁННУЮ цену (`gnomeUpgradeCost(fromLevel)`, config — NO-HARDCODE). Снимок-кэш НЕ
   * трогаем здесь — он проекция показа, НЕ источник серверной траты (AC-9); reconcile (load)
   * перетрёт server-truth'ом. Меняем значения НА МЕСТЕ (update/setState), без пересборки каркаса
   * → нет мигания `—`→число (AC-12 / P1 §0.A). Возвращает баланс ДО списания (для отката-страховки).
   */
  function applyOptimisticUpgrade(color: ColorIndex, fromLevel: number): void {
    const cost = gnomeUpgradeCost(fromLevel);
    currentBalance = {
      gold: Math.max(0, currentBalance.gold - cost.gold),
      shards: Math.max(0, currentBalance.shards - cost.shards),
    };
    hud.setBalances(currentBalance);
    const optimistic: GnomeState = {
      ...(gnomeStates.get(color) ?? { color, level: fromLevel, prestigeCount: 0, unlocked: true }),
      color,
      level: fromLevel + 1,
      unlocked: true,
    };
    gnomeStates.set(color, optimistic);
    cards.get(color)?.update(optimistic, cardOpts(optimistic));
    // setPending после update (update сбрасывает pending-класс) — кнопка остаётся в полёте.
    setGnomePending(color, true);
    refreshOpenDetail();
  }

  /**
   * Действие карточки/детали (прокачать / престиж) — связка intent → серверная мутация →
   * ресинк server-truth (один путь для ОБЕИХ поверхностей, AC-3/5/6/7/8/10/11). Эталон —
   * `changeDisplayName` + его потребитель (профиль): мутация через `services/meta-api`, затем
   * ресинк `GET /meta/state` через `getMetaStateShared()` (reconcile перетирает server-truth).
   *
   * Поток (общий «pending→mutate→resync», без копипаст-веток upgrade/prestige — выбор route
   * по `isMaxLevel(level)`):
   *   1. in-flight guard per-color (повтор во время полёта = no-op, НЕ второй запрос, AC-8).
   *   2. L20 (`isMaxLevel`) → confirm-dialog (необратимость §2.4); отмена → ничего не шлём,
   *      pending НЕ ставим (AC-10). Иначе апгрейд — оптимистик +1 сразу (AC-5).
   *   3. Серверная мутация (`upgradeGnome(color, expectedLevel)` / `prestigeGnome(color)`).
   *      `expectedLevel` = уровень из МОДЕЛИ `gnomeStates` (server-truth, не из DOM, AC-4 / CAS).
   *   4. После ЛЮБОГО ответа (ok / отказ / транспорт) — ресинк `load()` (reconcile, AC-6):
   *      server-truth перетирает баланс/состояние гнома/деталь. На ok-совпадении визуального
   *      скачка нет; на отказе/ошибке оптимистик откатывается к server-truth (AC-7/AC-16).
   */
  function handleAction(color: ColorIndex): void {
    if (destroyed) return;
    if (inFlight.has(color)) return; // AC-8: уже в полёте — не шлём второй запрос
    const st = gnomeStates.get(color);
    if (!st || !st.unlocked) return; // locked не качается (server-truth I-Gn7); защита
    if (isMaxLevel(st.level)) {
      void runPrestige(color);
    } else {
      void runUpgrade(color, st.level);
    }
  }

  /**
   * Ресинк server-truth после мутации (AC-6) — единый «resync»-хвост общего потока. Перечитывает
   * `GET /meta/state` через `load()` (тот же дедуп-путь, что reconcile при mount / смене identity):
   * балансы HUD + currentBalance + пул + состояние гнома (`applyGnomeState`/reconcile) + открытая
   * деталь (`refreshOpenDetail`) + снимок — всё из ОДНОГО источника. Снимает in-flight + pending
   * на ОБЕИХ поверхностях. На совпадении с оптимистикой — бесшумно; на расхождении/отказе —
   * откат к server-truth НА МЕСТЕ (без `—`→число, AC-7/AC-12).
   */
  async function resyncAfterMutation(color: ColorIndex): Promise<void> {
    try {
      // hadCache=true: каркас/балансы уже на экране (это дотягивание server-truth, не первый показ)
      // → load НЕ показывает «Загрузка…» и не мигает скелетом (AC-12).
      await load(true);
    } finally {
      inFlight.delete(color);
      if (!destroyed) {
        // reconcile (load → applyGnomeState → card.update) уже сбросил pending-класс и вернул
        // кнопку в server-truth-состояние; снимаем pending явно как страховку (идемпотентно)
        // для случая, когда load вышел рано (демо-route / отсутствие карточки).
        setGnomePending(color, false);
      }
    }
  }

  /** Апгрейд: оптимистик +1 → `upgradeGnome` (intent {color, expected_level}) → ресинк (AC-3/5/6/7). */
  async function runUpgrade(color: ColorIndex, expectedLevel: number): Promise<void> {
    inFlight.add(color);
    applyOptimisticUpgrade(color, expectedLevel);
    // expectedLevel — из server-truth-модели (CAS-гард, AC-4): сервер поднимет ТОЛЬКО при
    // совпадении; double-tap/рассинхрон → stale_level no-op (валюта не списана дважды).
    await upgradeGnome(color, expectedLevel);
    if (destroyed) return;
    // Любой исход (ok/отказ/транспорт) → ресинк перетирает server-truth (откат при отказе, AC-7).
    await resyncAfterMutation(color);
  }

  /** Престиж (L20): confirm → `prestigeGnome` (intent {color}) → ресинк (AC-10). */
  async function runPrestige(color: ColorIndex): Promise<void> {
    // Необратимо (§2.4): подтверждение ДО запроса. Отмена → ничего не шлём, pending не ставим.
    const confirmed = await confirmDialog({
      title: t('game.gnomes.prestigeTitle'),
      message: t('game.gnomes.prestigeMessage'),
      confirmLabel: t('game.gnomes.prestigeConfirm'),
      cancelLabel: t('game.gnomes.prestigeCancel'),
    });
    if (destroyed || !confirmed) return;
    if (inFlight.has(color)) return; // защита от гонки, пока висел confirm
    inFlight.add(color);
    setGnomePending(color, true);
    // Body РОВНО {color} (AC-9): престиж гейтится server-side natural-CAS по level==MAX.
    await prestigeGnome(color);
    if (destroyed) return;
    await resyncAfterMutation(color);
  }

  /** Открыть большую карточку-деталь гнома `color` (overlay) — drill-down, как у зверей. */
  function openGnomeDetail(color: ColorIndex): void {
    if (destroyed) return;
    // Одна деталь за раз: если открыта другая — закрыть.
    if (detail) detail.close();
    const handle = mountGnomeDetail({
      color,
      onAction: (c) => handleAction(c),
      onClose: () => {
        if (detail) detail.unmount();
        detail = null;
        openDetailColor = null;
      },
    });
    detail = handle;
    openDetailColor = color;
    handle.setState(detailStateFor(color));
    handle.open();
  }

  /** Собрать стейт детали для цвета (уровень/престиж/unlocked/веха/баланс) — один источник. */
  function detailStateFor(color: ColorIndex): GnomeDetailState {
    const st = gnomeStates.get(color);
    const unlocked = st?.unlocked ?? false;
    return {
      level: st?.level ?? 1,
      prestigeCount: st?.prestigeCount ?? 0,
      // locked → teaser-ветка детали (силуэт + «следующий гном на уровне N», без кнопок,
      // AC-7/AC-4). Число вехи — server-truth (NO-HARDCODE, AC-3).
      unlocked,
      ...(unlocked ? {} : { nextUnlockLevel: currentNextUnlock }),
      // Баланс игрока (П12 AC-12.4): контролируемый prop → деталь dim/disable «Прокачать».
      balance: currentBalance,
    };
  }

  /** Пере-применить стейт открытой детали (после reconcile баланса/гномов) — без пересборки. */
  function refreshOpenDetail(): void {
    if (!detail || openDetailColor == null) return;
    detail.setState(detailStateFor(openDetailColor));
  }

  /** Применить открытое состояние гнома к карточке + локальной модели (без анимации). */
  function applyGnomeState(g: GnomeState): void {
    gnomeStates.set(g.color, g);
    cards.get(g.color)?.update(g, cardOpts(g));
    // Карточка сменила высоту (locked-силуэт → открытая) → пере-проверить single-mode.
    relayout();
  }

  /**
   * Проиграть reveal-очередь для новооткрытых цветов (AC-14/17). Экран зовёт переиспользуемый
   * плеер `revealGnomes` (логика вспышки/очереди — в `lib/gnome-reveal`, AC-8: экран НЕ
   * содержит scale/transition-кода — только список слотов + onSettled финал-рендер). Порядок
   * = возрастание цвета (= индекс вехи, §5.1). reduce-motion → мгновенно (AC-16). Слоты, что
   * НЕ reveal'им, ставим финально без вспышки (AC-18 — пропущенный reveal: server-truth).
   *
   * @param next — финальная server-truth-модель 7 слотов.
   * @param revealColors — цвета, для которых играть вспышку (диф prev→next), по возрастанию.
   */
  async function reconcileGnomes(
    next: readonly GnomeState[],
    revealColors: readonly number[],
  ): Promise<void> {
    const revealSet = new Set(revealColors);
    // Сначала применить финал ко ВСЕМ слотам, КРОМЕ тех, что будут reveal'иться (их финал
    // ставит onSettled). Иначе слот «вщёлкнулся» бы открытым до вспышки.
    for (const g of next) {
      if (!revealSet.has(g.color)) applyGnomeState(g);
    }
    if (revealColors.length === 0) return;

    // Слот-элементы reveal'имых цветов В ПОРЯДКЕ очереди (= revealColors). Модель уже = финал;
    // вид доводит onSettled (силуэт → открытый рендер карточки) по завершении вспышки.
    const slots: RevealSlot[] = [];
    const finalByElement = new Map<RevealSlot, GnomeState>();
    for (const color of revealColors) {
      const g = next.find((x) => x.color === color);
      const card = cards.get(color as ColorIndex);
      if (!g || !card) continue;
      gnomeStates.set(g.color, g); // модель — финал сразу (server-truth)
      const slot = card.element as unknown as RevealSlot;
      slots.push(slot);
      finalByElement.set(slot, g);
    }
    await revealGnomes(slots, {
      reduceMotion: prefersReducedMotion(),
      onSettled: (slot) => {
        if (destroyed) return;
        const g = finalByElement.get(slot);
        if (g) cards.get(g.color)?.update(g, cardOpts(g)); // открытый рендер карточки
      },
    });
  }

  /**
   * Демо-хук reveal с заморозкой mid-кадра (§0.D screenshots-DoD, AC-26 г). `?reveal=<colors>`
   * → проигрываем вспышку для тех цветов; `&freeze=mid` → останавливаем на mid-кадре (слот в
   * классе `gnome-card--revealing`) и ставим `window.__gnomeRevealResume()` для продолжения.
   * Это ДЕМО-управление поверх плеера (не дублирует scale/transition — только класс),
   * детерминизирует скриншот. Возвращает true, если демо-reveal обработан.
   */
  function runDemoReveal(next: readonly GnomeState[]): boolean {
    const colors = parseRevealColors();
    if (colors.length === 0) return false;
    const freezeMid = queryParam('freeze') === 'mid';

    if (freezeMid) {
      // Заморозка: слоты reveal'имых цветов — в анимирующем классе (mid-кадр), финал
      // применяем ТОЛЬКО по resume. Соседи не двигаются (transform на слое, AC-15).
      for (const color of colors) {
        const card = cards.get(color as ColorIndex);
        if (card) card.element.classList.add(REVEALING_CLASS);
      }
      (window as unknown as { __gnomeRevealResume?: () => void }).__gnomeRevealResume = () => {
        for (const color of colors) {
          const card = cards.get(color as ColorIndex);
          card?.element.classList.remove(REVEALING_CLASS);
          const g = next.find((x) => x.color === color);
          if (g && card) {
            gnomeStates.set(g.color, g);
            card.update(g, cardOpts(g));
          }
        }
      };
      return true;
    }

    // Обычный демо-reveal: проиграть очередь (вспышка → финал).
    void reconcileGnomes(next, colors);
    return true;
  }

  /**
   * Reconcile server-truth (каркас гномов уже в DOM — НЕ гейтим на await). Дотягивает
   * балансы/пул + СОСТОЯНИЕ ГНОМОВ (server-truth, I-Gn4) + reveal новооткрытых (диф к
   * предыдущей модели). `hadCache` — был ли мгновенный кэш-показ.
   */
  async function load(hadCache: boolean): Promise<void> {
    if (!hadCache) setScreenState(t('game.gnomes.loading'));

    // §C (owner-bug 2026-06-22): ДОЖДАТЬСЯ резолва identity ПЕРЕД reconcile — паттерн Профиля
    // (profile-screen.ts:890-898). На F5 mount синхронен ДО `bootIdentity()` (IndexedDB async):
    // снимок keyed по playerId вернул null лишь из-за нерезолвленной identity, не из-за «нет
    // прогресса». bootIdentity идемпотентен (общий промис) — на тёплых заходах резолвится мгновенно.
    // Bearer гарантированно приложится к `GET /meta/state` (нет 401-гонки). КАРТОЧКИ не вставляем
    // здесь: их вид становится достоверным только по ответу сервера (open у авторизованного / 401
    // у гостя) — до этого awaiting-gate держит их вне DOM (НЕ мелькаем placeholder all-locked).
    await bootIdentity();
    if (destroyed) return;

    const res = await getMetaStateShared();
    if (destroyed) return;
    // companion-focus (AC-8): server ответил (ok ИЛИ 401) → состояние карточек ДОСТОВЕРНО.
    // С этого момента deep-link фокус может применяться (на final-состоянии, не на placeholder).
    serverReconciled = true;
    if (!res.ok) {
      // Снять скелет: данных не будет (гость/ошибка) → не вечный скелет.
      root.classList.remove('gnomes-screen--awaiting');
      // §C: ответ получен (пусть и 401) → вид ДОСТОВЕРЕН. Для настоящего гостя (unauthenticated)
      // placeholder all-locked КОРРЕКТЕН (AC-C.3) → показать карточки. На сетевой ошибке с кэшем
      // cardsShown уже true (снимок показал); без кэша — показываем placeholder (балансы
      // недоступны, помощники видны locked, как было — деградация, не вечный скелет).
      viewReconciled = true;
      tryRevealCards();
      if (res.error === 'unauthenticated') {
        // Гость: гномы (placeholder = все locked) видны, балансы — нули (HUD сам dim).
        setScreenState(null);
      } else if (!hadCache) {
        setScreenState(t('game.gnomes.balancesError'));
      } else {
        setScreenState(null);
      }
      return;
    }
    setScreenState(null);
    root.classList.remove('gnomes-screen--awaiting'); // плавное проявление динамики (fade)
    hud.setBalances({ gold: res.gold, shards: res.shards });
    currentBalance = { gold: res.gold, shards: res.shards }; // server-truth для dim/disable (П12)
    refreshOpenDetail(); // если деталь открыта — пере-применить с новым балансом
    gemHud.setPool(res.gemPool);

    // Строка «следующий гном на уровне N» — server-truth число вехи (NO-HARDCODE, AC-3/19).
    setNextUnlock(res.nextUnlockLevel);

    // helper-unlock A2 (AC-10): переподтвердить last-seen свежим server-truth granted (игрок
    // на экране — всё «просмотрено»). Бейдж остаётся снятым; getMetaStateShared уже эмитнул
    // granted → рейл сравнит с обновлённым last-seen и dot не вернётся.
    resetBadge(res.gnomeUnlocksGranted);

    // СОСТОЯНИЕ ГНОМОВ (server-truth, I-Gn4). Демо-state (`?state=...`) фиксирует вид для
    // baseline и НЕ перетирается сервером (детерминизм скриншота). Демо-reveal (`?reveal=`)
    // имеет приоритет (управляемая вспышка). Иначе — server-truth + reveal по дифу.
    const next = gnomesFromServer(res.gnomes);
    const demoState = queryParam('state');

    if (runDemoReveal(next)) return; // демо-reveal / freeze обработал слоты
    if (demoState === 'locked' || demoState === 'partial' || demoState === 'allopen' || demoState === 'mix') {
      return; // демо-baseline зафиксирован initialGnomes — сервер вид не трогает
    }

    // §C: ПЕРВЫЙ показ (карточки ещё не в DOM — холодный F5 без валидного снимка). Применяем
    // server-truth НАПРЯМУЮ (без reveal-вспышки: вспышка — для гнома, открывшегося ПОКА игрок
    // смотрит, а тут это первый кадр, «открывать из ничего» нечего) → карточки появляются ОДНИМ
    // кадром в реальном виде. Это и устраняет ложный all-locked-flash: до этого момента
    // awaiting-gate держал их вне DOM, теперь вид достоверен (server ответил).
    if (!cardsShown) {
      for (const g of next) applyGnomeState(g);
      viewReconciled = true;
      tryRevealCards();
      return;
    }

    // Прод (карточки уже показаны): диф prev→next → reveal новооткрытых (AC-14/17), остальные —
    // финал без вспышки. Это сценарий «гном открылся, пока игрок на экране» (reveal-анимация).
    const prev = [...gnomeStates.values()].sort((a, b) => a.color - b.color);
    const newly = newlyUnlockedColors(prev, next);
    await reconcileGnomes(next, newly);
  }

  // §C: реакция на смену идентичности (вход/выход) — снимок keyed по playerId стал другим →
  // ПЕРЕ-читать реальное состояние гномов + reconcile (как Профиль reloadAfterIdentityChange).
  // Сигнал приходит ПОСЛЕ резолва bootIdentity (main.ts emitIdentityChanged) → identity известна.
  // Демо-route не трогаем (вид fix'ед для baseline). Идемпотентно: load() сам await-bootIdentity.
  const unsubscribeIdentity = onIdentityChanged(() => {
    if (destroyed || hasDemoRoute) return;
    const snap = loadMetaSnapshot();
    if (snap) {
      // Новый аккаунт имеет per-player снимок → реальное состояние сразу (вид достоверен),
      // карточки можно показать немедленно (как на тёплом mount со снимком).
      rehydrateFromSnapshot();
      viewReconciled = true;
      tryRevealCards();
    }
    // Если снимка нет — карточки покажутся по ответу нового load() (server / 401), awaiting-gate
    // держит их вне DOM до достоверности (тот же §C-инвариант, что на холодном F5).
    void load(snap !== null);
  });

  // Reconcile (каркас + кэш уже в DOM — это server-truth дотягивание балансов/пула).
  void load(snapshot !== null).then(() => {
    if (destroyed) return;
    // companion-focus: первый reconcile завершён (incl. reveal-очередь — она awaited внутри load)
    // → состояние/позиции карточек финальны. Применяем deep-link фокус (one-shot, no-op если уже
    // применён из revealCards на cold-path, AC-8). Делаем ДО detail-хука — фокус ≠ деталь (AC-4).
    applyFocus();
    // Demo/viewport-хук: `?detail=<color>` авто-открывает деталь гнома для screenshot-
    // baseline (skill §0.D), как `?detail=<type>` у Мастерской. В проде параметр отсутствует.
    try {
      const detailParam = new URLSearchParams(window.location.search).get('detail');
      const c = detailParam ? Number(detailParam) : NaN;
      if (Number.isInteger(c) && c >= 1 && c <= 7) openGnomeDetail(c as ColorIndex);
    } catch {
      // нет window — no-op.
    }
  });

  return {
    unmount(): void {
      destroyed = true;
      unsubscribeIdentity();
      ro.disconnect();
      titleFit.detach();
      infoPopover.unmount();
      hud.unmount();
      gemHud.unmount();
      if (detail) detail.unmount();
      detail = null;
      // companion-focus: снять ВИЗУАЛ + слушатели/таймер БЕЗ потребления store (clearFocusHighlight,
      // не dismissFocus): если экран снимают на boot re-mount, пока цель не «прожита», store держит
      // её → выживший re-mount пере-нацелится. Потребление — только при настоящем dismiss (AC-3).
      clearFocusHighlight();
      for (const c of cards.values()) c.destroy();
      // Снять демо-хук resume (если ставился) — не оставлять глобал между экранами.
      try {
        delete (window as unknown as { __gnomeRevealResume?: () => void }).__gnomeRevealResume;
      } catch {
        // нет window — no-op.
      }
      host.innerHTML = '';
    },
  };
}
